import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '../../assets/styles/CountryBattle.css';
import { API_BASE_URL } from '../../config/api';

// Import battle sound
import battleSound from '../../assets/media/document_6183896695919613582 (online-audio-converter.com).mp3';

// Default fallback country data with flag URLs
import { DEFAULT_COUNTRIES, ARENA_FULL_THRESHOLD, ARENA_RADIUS, DIFFICULTY_SETTINGS, ARENA_SHAPES, THEMES } from './countryBattleData';
import CountryBattleResult from './CountryBattleResult';
import CountrySelectModal from './CountrySelectModal';

const CountryBattle = () => {
  const [gameState, setGameState] = useState('selection');
  const [selectedTeams, setSelectedTeams] = useState([]); // Array of up to 4 teams
  const [winner, setWinner] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [showCountrySelect, setShowCountrySelect] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [collisionEffects, setCollisionEffects] = useState([]);
  const [teamCounts, setTeamCounts] = useState({}); // Object with team id as key
  const [renderTick, setRenderTick] = useState(0); // eslint-disable-line no-unused-vars
  const [isMuted, setIsMuted] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(THEMES[0]);
  const [eventName, setEventName] = useState(''); // User input for event name
  const [eventType, setEventType] = useState(''); // User input for event type
  const [arenaShape, setArenaShape] = useState('circle'); // 'circle' or 'square'
  const [difficulty, setDifficulty] = useState('medium'); // 'easy', 'medium', 'hard'
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES); // Dynamic countries list
  
  // Use refs for game state to avoid re-render issues in animation loop
  const playersRef = useRef([]);
  const ballRef = useRef({ x: 50, y: 30 });
  const gameRunningRef = useRef(false);
  const animationFrameRef = useRef(null);
  const playerIdRef = useRef(0);
  const selectedTeamsRef = useRef([]);
  const audioRef = useRef(null);
  const arenaShapeRef = useRef('circle');
  const difficultyRef = useRef('medium');

  // Fetch countries from API on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/country-battle/teams?activeOnly=true`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Transform API data to match expected format
            const transformedCountries = data.map(team => ({
              id: team.code.toLowerCase(),
              name: team.name,
              flag: team.flag,
              code: team.code
            }));
            setCountries(transformedCountries);
          }
        }
      } catch (error) {
        // Silently fall back to default countries
        console.log('Using default countries');
      }
    };
    fetchCountries();
  }, []);

  // Sync refs
  useEffect(() => {
    arenaShapeRef.current = arenaShape;
    difficultyRef.current = difficulty;
  }, [arenaShape, difficulty]);

  // Team colors for different teams
  const teamColors = ['#00e676', '#ff5722', '#2196f3', '#e91e63'];

  // Sync team refs
  useEffect(() => {
    selectedTeamsRef.current = selectedTeams;
  }, [selectedTeams]);

  // Generate new player
  const createPlayer = useCallback((teamIndex, flag, name, teamId, startX = null, startY = null) => {
    playerIdRef.current += 1;
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.8 + Math.random() * 0.6;
    
    let x, y;
    if (startX !== null && startY !== null) {
      x = startX + (Math.random() - 0.5) * 10;
      y = startY + (Math.random() - 0.5) * 10;
    } else {
      const spawnAngle = Math.random() * Math.PI * 2;
      const spawnRadius = Math.random() * 25 + 10;
      x = 50 + Math.cos(spawnAngle) * spawnRadius;
      y = 50 + Math.sin(spawnAngle) * spawnRadius;
    }
    
    return {
      id: `player-${playerIdRef.current}`,
      teamIndex,
      teamId,
      flag,
      name,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
    };
  }, []);

  // Generate ball position
  const generateBallPosition = useCallback(() => {
    const angle = Math.random() * Math.PI * 2;
    const radius = Math.random() * 20 + 10;
    return {
      x: 50 + Math.cos(angle) * radius,
      y: 50 + Math.sin(angle) * radius,
    };
  }, []);

  // Check collision between two objects
  const checkCollision = useCallback((x1, y1, x2, y2, radius) => {
    const dx = x1 - x2;
    const dy = y1 - y2;
    return Math.sqrt(dx * dx + dy * dy) < radius;
  }, []);

  // Add collision effect
  const addCollisionEffect = useCallback((x, y, team) => {
    const id = `effect-${Date.now()}`;
    setCollisionEffects(prev => [...prev, { id, x, y, team }]);
    setTimeout(() => {
      setCollisionEffects(prev => prev.filter(e => e.id !== id));
    }, 600);
  }, []);

  // Main game loop using requestAnimationFrame
  const gameLoop = useCallback(() => {
    if (!gameRunningRef.current) return;

    const players = playersRef.current;
    const ball = ballRef.current;
    const currentShape = arenaShapeRef.current;
    const currentDifficulty = DIFFICULTY_SETTINGS[difficultyRef.current];
    let ballHit = false;
    let hitPlayer = null;

    // Collision radius based on difficulty
    const FLAG_RADIUS = 3 * currentDifficulty.collisionMultiplier;
    const BALL_RADIUS = 1.8 * currentDifficulty.collisionMultiplier;
    const PLAYER_COLLISION_RADIUS = 4; // Radius for player-to-player collision

    // Update each player position
    for (let i = 0; i < players.length; i++) {
      const player = players[i];
      
      // Update position with difficulty-based speed
      player.x += player.vx;
      player.y += player.vy;

      if (currentShape === 'circle') {
        // Circle boundary collision
        const dx = player.x - 50;
        const dy = player.y - 50;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > ARENA_RADIUS) {
          // Bounce off wall
          const angle = Math.atan2(dy, dx);
          player.x = 50 + Math.cos(angle) * ARENA_RADIUS;
          player.y = 50 + Math.sin(angle) * ARENA_RADIUS;
          
          // Reflect velocity
          const normalX = dx / distance;
          const normalY = dy / distance;
          const dot = player.vx * normalX + player.vy * normalY;
          player.vx = player.vx - 2 * dot * normalX;
          player.vy = player.vy - 2 * dot * normalY;
          
          // Add slight randomness
          player.vx += (Math.random() - 0.5) * 0.2;
          player.vy += (Math.random() - 0.5) * 0.2;
        }
      } else {
        // Square boundary collision
        const minBound = 7;
        const maxBound = 93;
        
        // Left/Right walls
        if (player.x <= minBound) {
          player.x = minBound;
          player.vx = -player.vx;
          player.vx += (Math.random() - 0.5) * 0.2;
        } else if (player.x >= maxBound) {
          player.x = maxBound;
          player.vx = -player.vx;
          player.vx += (Math.random() - 0.5) * 0.2;
        }
        
        // Top/Bottom walls
        if (player.y <= minBound) {
          player.y = minBound;
          player.vy = -player.vy;
          player.vy += (Math.random() - 0.5) * 0.2;
        } else if (player.y >= maxBound) {
          player.y = maxBound;
          player.vy = -player.vy;
          player.vy += (Math.random() - 0.5) * 0.2;
        }
      }

      // Player-to-player collision detection (balls bouncing off each other)
      for (let j = i + 1; j < players.length; j++) {
        const other = players[j];
        const dx = player.x - other.x;
        const dy = player.y - other.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        
        if (dist < PLAYER_COLLISION_RADIUS && dist > 0) {
          // Normalize collision vector
          const nx = dx / dist;
          const ny = dy / dist;
          
          // Relative velocity
          const dvx = player.vx - other.vx;
          const dvy = player.vy - other.vy;
          
          // Relative velocity in collision normal direction
          const dvn = dvx * nx + dvy * ny;
          
          // Only resolve if objects are moving towards each other
          if (dvn < 0) {
            // Update velocities (elastic collision)
            player.vx -= dvn * nx;
            player.vy -= dvn * ny;
            other.vx += dvn * nx;
            other.vy += dvn * ny;
            
            // Separate the balls to prevent sticking
            const overlap = PLAYER_COLLISION_RADIUS - dist;
            player.x += nx * overlap * 0.5;
            player.y += ny * overlap * 0.5;
            other.x -= nx * overlap * 0.5;
            other.y -= ny * overlap * 0.5;
            
            // Add slight randomness for more dynamic movement
            player.vx += (Math.random() - 0.5) * 0.3;
            player.vy += (Math.random() - 0.5) * 0.3;
            other.vx += (Math.random() - 0.5) * 0.3;
            other.vy += (Math.random() - 0.5) * 0.3;
          }
        }
      }

      // Maintain consistent speed based on difficulty
      const speed = Math.sqrt(player.vx * player.vx + player.vy * player.vy);
      const targetSpeed = currentDifficulty.speed;
      if (speed !== 0) {
        player.vx = (player.vx / speed) * targetSpeed;
        player.vy = (player.vy / speed) * targetSpeed;
      }

      // Check ball collision
      if (!ballHit && checkCollision(player.x, player.y, ball.x, ball.y, FLAG_RADIUS + BALL_RADIUS)) {
        ballHit = true;
        hitPlayer = { ...player };
      }
    }

    // Handle ball hit - duplicate flag and move ball
    if (ballHit && hitPlayer) {
      addCollisionEffect(ball.x, ball.y, hitPlayer.teamIndex);
      
      // Create duplicate player
      const newPlayer = createPlayer(
        hitPlayer.teamIndex,
        hitPlayer.flag,
        hitPlayer.name,
        hitPlayer.teamId,
        ball.x,
        ball.y
      );
      playersRef.current = [...playersRef.current, newPlayer];
      
      // Move ball to new position
      const newBallPos = generateBallPosition();
      ballRef.current = newBallPos;
      
      // Update counts for all teams
      const counts = {};
      selectedTeamsRef.current.forEach((team, index) => {
        counts[team.id] = playersRef.current.filter(p => p.teamId === team.id).length;
      });
      setTeamCounts(counts);
      
      // Calculate total balls in arena
      const totalBalls = playersRef.current.length;
      
      // Check for winner: arena is getting crowded
      const arenaFull = totalBalls >= ARENA_FULL_THRESHOLD;
      
      if (arenaFull) {
        gameRunningRef.current = false;
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        
        // Stop audio
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        // Find the team with most balls as winner
        let finalWinner = null;
        let maxCount = 0;
        selectedTeamsRef.current.forEach(team => {
          if (counts[team.id] > maxCount) {
            maxCount = counts[team.id];
            finalWinner = team;
          }
        });
        
        setTimeout(() => {
          setWinner(finalWinner);
          setGameState('result');
        }, 500);
        return;
      }
    }

    // Trigger re-render
    setRenderTick(prev => prev + 1);

    // Continue loop
    animationFrameRef.current = requestAnimationFrame(gameLoop);
  }, [checkCollision, addCollisionEffect, createPlayer, generateBallPosition]);

  // Start game
  const startGame = useCallback(() => {
    if (selectedTeams.length < 2) return;

    // Initialize players - one for each team
    const initialPlayers = selectedTeams.map((team, index) => 
      createPlayer(index, team.flag, team.name, team.id)
    );
    playersRef.current = initialPlayers;

    // Initialize ball
    ballRef.current = generateBallPosition();

    // Reset state - initialize counts for each team
    const initialCounts = {};
    selectedTeams.forEach(team => {
      initialCounts[team.id] = 1;
    });
    setTeamCounts(initialCounts);
    setWinner(null);
    setCollisionEffects([]);
    setGameState('playing');
    
    // Start audio
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.loop = true;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
    
    // Start game loop
    gameRunningRef.current = true;
    animationFrameRef.current = requestAnimationFrame(gameLoop);
  }, [selectedTeams, createPlayer, generateBallPosition, gameLoop]);

  // Reset game
  const resetGame = useCallback(() => {
    gameRunningRef.current = false;
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);

    // Stop audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    playersRef.current = [];
    ballRef.current = { x: 50, y: 50 };
    
    setGameState('selection');
    setSelectedTeams([]);
    setPrediction(null);
    setWinner(null);
    setCollisionEffects([]);
    setTeamCounts({});
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      gameRunningRef.current = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  // Filter countries
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Country select modal
  // Selection screen
  const renderSelectionScreen = () => (
    <div className="selection-screen">
      <h1 className="game-title" style={{ color: selectedTheme.accentColor }}>
        <span className="title-icon">⚔️</span>
        COUNTRY BATTLE
        <span className="title-icon">⚔️</span>
      </h1>
      <p className="game-subtitle">Choose 2-4 teams and watch them battle!</p>

      {/* Theme Selection */}
      <div className="theme-section">
        <h3 className="section-title">🎨 CHOOSE THEME</h3>
        <div className="theme-options">
          {THEMES.map(theme => (
            <button
              key={theme.id}
              className={`theme-btn ${selectedTheme.id === theme.id ? 'active' : ''}`}
              onClick={() => setSelectedTheme(theme)}
              style={{
                background: theme.bgGradient,
                borderColor: theme.arenaBorder,
              }}
            >
              <span className="theme-icon">{theme.icon}</span>
              <span className="theme-name">{theme.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Arena Shape Selection */}
      <div className="shape-section">
        <h3 className="section-title">🔲 ARENA SHAPE</h3>
        <div className="shape-options">
          {ARENA_SHAPES.map(shape => (
            <button
              key={shape.id}
              className={`shape-btn ${arenaShape === shape.id ? 'active' : ''}`}
              onClick={() => setArenaShape(shape.id)}
              style={arenaShape === shape.id ? { borderColor: selectedTheme.accentColor, boxShadow: `0 0 15px ${selectedTheme.accentColor}80` } : {}}
            >
              <span className="shape-icon">{shape.icon}</span>
              <span className="shape-name">{shape.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Difficulty Selection */}
      <div className="difficulty-section">
        <h3 className="section-title">⚡ DIFFICULTY MODE</h3>
        <div className="difficulty-options">
          {Object.entries(DIFFICULTY_SETTINGS).map(([key, setting]) => (
            <button
              key={key}
              className={`difficulty-btn ${difficulty === key ? 'active' : ''}`}
              onClick={() => setDifficulty(key)}
              style={difficulty === key ? { borderColor: selectedTheme.accentColor, boxShadow: `0 0 15px ${selectedTheme.accentColor}80` } : {}}
            >
              <span className="difficulty-icon">{setting.icon}</span>
              <span className="difficulty-name">{setting.name}</span>
            </button>
          ))}
        </div>
        <p className="difficulty-hint">
          {difficulty === 'easy' && '🐢 Slower balls, easier to predict collisions'}
          {difficulty === 'medium' && '🏃 Balanced speed and collision rate'}
          {difficulty === 'hard' && '🚀 Fast balls, less collision - more challenge!'}
        </p>
      </div>

      {/* Event Name Input */}
      <div className="event-input-section">
        <h3 className="section-title">📝 EVENT NAME (OPTIONAL)</h3>
        <input
          type="text"
          className="event-input"
          placeholder="Enter event name (e.g., World Cup 2024)"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          maxLength={50}
          style={{ borderColor: selectedTheme.accentColor }}
        />
      </div>

      {/* Event Type Input */}
      <div className="event-input-section">
        <h3 className="section-title">🏆 EVENT TYPE (OPTIONAL)</h3>
        <input
          type="text"
          className="event-input"
          placeholder="Enter event type (e.g., Final Match, Semi Final)"
          value={eventType}
          onChange={(e) => setEventType(e.target.value)}
          maxLength={50}
          style={{ borderColor: selectedTheme.accentColor }}
        />
      </div>

      {/* Selected Teams Display */}
      <div className="selected-teams-container">
        {[0, 1, 2, 3].map(index => (
          <div 
            key={index}
            className={`team-slot ${selectedTeams[index] ? 'filled' : 'empty'}`}
            onClick={() => setShowCountrySelect(true)}
            style={selectedTeams[index] ? { borderColor: teamColors[index] } : {}}
          >
            {selectedTeams[index] ? (
              <>
                <img src={selectedTeams[index].flag} alt={selectedTeams[index].name} className="slot-flag" />
                <span className="slot-name">{selectedTeams[index].name}</span>
                <button 
                  className="remove-team-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTeams(selectedTeams.filter((_, i) => i !== index));
                  }}
                >
                  ✕
                </button>
              </>
            ) : (
              <div className="slot-placeholder">
                <span className="plus-icon">+</span>
                <span>{index < 2 ? 'Required' : 'Optional'}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <button 
        className="add-teams-btn"
        onClick={() => setShowCountrySelect(true)}
        style={{ background: `linear-gradient(135deg, ${selectedTheme.accentColor}, ${selectedTheme.arenaBorder})` }}
      >
        {selectedTeams.length === 0 ? 'SELECT COUNTRIES' : 'ADD/REMOVE COUNTRIES'}
      </button>

      {/* Prediction */}
      {selectedTeams.length >= 2 && (
        <div className="prediction-section">
          <h3 className="section-title">🎯 PREDICT THE WINNER</h3>
          <div className="prediction-options">
            {selectedTeams.map((team, index) => (
              <button
                key={team.id}
                className={`prediction-btn ${prediction === team.id ? 'active' : ''}`}
                onClick={() => setPrediction(team.id)}
                style={prediction === team.id ? { borderColor: teamColors[index] } : {}}
              >
                <img src={team.flag} alt={team.name} />
                <span>{team.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Start Button */}
      <button
        className={`start-battle-btn ${selectedTeams.length >= 2 ? 'ready' : 'disabled'}`}
        onClick={startGame}
        disabled={selectedTeams.length < 2}
        style={selectedTeams.length >= 2 ? { background: `linear-gradient(135deg, ${selectedTheme.accentColor}, ${selectedTheme.arenaBorder})` } : {}}
      >
        <span className="btn-text">START BATTLE</span>
        <span className="btn-icon">🏆</span>
      </button>
    </div>
  );

  // Playing screen
  const renderPlayingScreen = () => {
    const players = playersRef.current;
    const ball = ballRef.current;

    const toggleMute = () => {
      if (audioRef.current) {
        audioRef.current.muted = !audioRef.current.muted;
        setIsMuted(!isMuted);
      }
    };

    return (
      <div className="playing-screen">
        {/* Mute Button */}
        <button 
          className="mute-btn" 
          onClick={toggleMute}
          style={{ borderColor: selectedTheme.accentColor }}
        >
          {isMuted ? '🔇' : '🔊'}
        </button>

        {/* Header with team flags only */}
        <div className="game-header-flags">
          {selectedTeams.map((team, index) => (
            <React.Fragment key={team.id}>
              <div className="flag-item">
                <img 
                  src={team.flag} 
                  alt={team.name} 
                  className="header-flag"
                  style={{ borderColor: teamColors[index] }}
                />
              </div>
              {index < selectedTeams.length - 1 && (
                <div 
                  className="vs-text"
                  style={{ color: selectedTheme.accentColor }}
                >
                  VS
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Event Name - Above Arena */}
        {eventName && (
          <div 
            className="event-name-label"
            style={{ 
              color: selectedTheme.accentColor,
              textShadow: `0 0 20px ${selectedTheme.accentColor}80`,
              fontWeight: 'bold'
            }}
          >
            {eventName}
          </div>
        )}

        {/* Arena */}
        <div className={`arena-container ${arenaShape === 'square' ? 'square-arena' : ''}`}>
          <div 
            className={`arena ${arenaShape === 'square' ? 'square' : ''}`}
            style={{
              background: selectedTheme.arenaGradient,
              borderColor: selectedTheme.arenaBorder,
              boxShadow: `0 0 40px ${selectedTheme.arenaBorder}80, 0 0 80px ${selectedTheme.arenaBorder}33, inset 0 0 60px rgba(0, 0, 0, 0.8)`,
              borderRadius: arenaShape === 'square' ? '20px' : '50%',
            }}
          >
            <div className={`arena-border ${arenaShape === 'square' ? 'square' : ''}`} style={{ borderColor: `${selectedTheme.arenaBorder}33` }}></div>
            <div className="arena-inner-glow"></div>

            {/* Target Ball - white/green style like in the image */}
            <div
              className="target-ball"
              style={{
                left: `${ball.x}%`,
                top: `${ball.y}%`,
              }}
            />

            {/* Moving Flag Balls */}
            {players.map(player => (
              <div
                key={player.id}
                className="moving-flag-ball"
                style={{
                  left: `${player.x}%`,
                  top: `${player.y}%`,
                  '--team-color': teamColors[player.teamIndex] || '#00e676',
                }}
              >
                <img src={player.flag} alt={player.name} />
              </div>
            ))}

            {/* Collision Effects */}
            <AnimatePresence>
              {collisionEffects.map(effect => (
                <motion.div
                  key={effect.id}
                  className="collision-effect"
                  style={{
                    left: `${effect.x}%`,
                    top: `${effect.y}%`,
                    color: teamColors[effect.team] || '#00e676',
                  }}
                  initial={{ scale: 0.5, opacity: 1 }}
                  animate={{ scale: 3, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="collision-text">+1</span>
                </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Event Type - Below Arena */}
            {eventType && (
              <div 
                className="event-type-label"
                style={{ 
                  background: `linear-gradient(135deg, ${selectedTheme.accentColor}, ${selectedTheme.arenaBorder})`,
                  boxShadow: `0 4px 15px ${selectedTheme.accentColor}80`
                }}
              >
                {eventType}
              </div>
            )}
          </div>

        {/* Live Score at Bottom - All Teams with Flags and Points */}
        <div 
          className="bottom-score-display"
          style={{ borderColor: `${selectedTheme.accentColor}33` }}
        >
          {selectedTeams.map((team, index) => (
            <React.Fragment key={team.id}>
              <div className="score-item">
                <img 
                  src={team.flag} 
                  alt={team.name} 
                  className="score-flag"
                  style={{ borderColor: teamColors[index] }}
                />
                <span 
                  className="score-count"
                  style={{ color: teamColors[index] }}
                >
                  {teamCounts[team.id] || 1}
                </span>
              </div>
              {index < selectedTeams.length - 1 && (
                <div 
                  className="score-separator"
                  style={{ color: selectedTheme.accentColor }}
                >
                  |
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        
        {/* Win Target Info */}
        <div 
          className="win-target"
          style={{ 
            background: `${selectedTheme.accentColor}22`,
            borderColor: `${selectedTheme.accentColor}55`
          }}
        >
          Arena fills at {ARENA_FULL_THRESHOLD} balls - Most balls wins!
        </div>
      </div>
    );
  };

  // Result screen
  const renderResultScreen = () => (
    <CountryBattleResult
      winner={winner}
      teamCounts={teamCounts}
      selectedTeams={selectedTeams}
      prediction={prediction}
      onPlayAgain={resetGame}
    />
  );

  return (
    <div 
      className="country-battle"
      style={{ 
        background: selectedTheme.bgGradient,
        '--theme-accent': selectedTheme.accentColor,
        '--theme-border': selectedTheme.arenaBorder,
      }}
    >
      {gameState !== 'result' && (
        <Link to="/" className="back-button">
          <span>←</span>
          <span className="sm:hidden">Back</span>
          <span className="hidden sm:inline">Back to Home</span>
        </Link>
      )}

      {/* Audio Element */}
      <audio ref={audioRef} src={battleSound} preload="auto" />

      {gameState === 'selection' && renderSelectionScreen()}
      {gameState === 'playing' && renderPlayingScreen()}
      {gameState === 'result' && renderResultScreen()}

      <AnimatePresence>
        {showCountrySelect && (
          <CountrySelectModal
            selectedTeams={selectedTeams}
            setSelectedTeams={setSelectedTeams}
            filteredCountries={filteredCountries}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onClose={() => setShowCountrySelect(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CountryBattle;
